// AgentInspectorDrawer: lateral drawer per agent.
// - Real chat to /functions/v1/agent-chat
// - Last 20 conversations
// - Model config (read-only summary based on neuralStore)
// - Stats from agent_stats
// - "Inspect Memory" shows current cached context (last reply + tokens)
import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Send, Sparkles, History, Settings as Cog, BarChart3, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Agent } from "@/store/neuralStore";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ChatMsg { role: "user" | "assistant"; content: string; ts?: number; tokens?: number; cost?: number; latency_ms?: number }

interface AgentStats {
  total_calls: number;
  total_success: number;
  total_failures: number;
  avg_latency_ms: number;
  avg_cost_usd: number;
  total_cost_usd: number;
  total_tokens: number;
  last_called_at: string | null;
}

interface ConvRow {
  id: string;
  model: string | null;
  total_tokens: number;
  total_cost_usd: number;
  updated_at: string;
  messages: ChatMsg[];
}

export function AgentInspectorDrawer({
  agent,
  open,
  onClose,
}: {
  agent: Agent | null;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState<string | null>(null);
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [history, setHistory] = useState<ConvRow[]>([]);
  const [memoryContext, setMemoryContext] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset when switching agents
  useEffect(() => {
    if (!agent) return;
    setChat([]);
    setInput("");
    setConvId(null);
    setMemoryContext("");
  }, [agent?.id]);

  // Load stats + history on open
  useEffect(() => {
    if (!open || !agent || !user) return;
    (async () => {
      const [{ data: s }, { data: h }] = await Promise.all([
        supabase.from("agent_stats").select("*").eq("user_id", user.id).eq("agent_id", agent.id).maybeSingle(),
        supabase.from("agent_conversations")
          .select("id, model, total_tokens, total_cost_usd, updated_at, messages")
          .eq("user_id", user.id).eq("agent_id", agent.id)
          .order("updated_at", { ascending: false }).limit(20),
      ]);
      setStats(s as AgentStats | null);
      setHistory((h as unknown as ConvRow[]) ?? []);
    })();
  }, [open, agent?.id, user?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chat.length]);

  if (!agent) return null;

  const send = async () => {
    const msg = input.trim();
    if (!msg) {
      toast.error("Digite uma mensagem");
      return;
    }
    if (!user) {
      toast.error("Faça login primeiro");
      return;
    }
    setInput("");
    const next: ChatMsg = { role: "user", content: msg, ts: Date.now() };
    setChat((c) => [...c, next]);
    setSending(true);
    try {
      const history20 = chat.slice(-20).map((m) => ({ role: m.role, content: m.content }));
      const { data, error } = await supabase.functions.invoke("agent-chat", {
        body: {
          agent_id: agent.id,
          agent_role: agent.role,
          message: msg,
          history: history20,
          conversation_id: convId,
          model: agent.models[0],
        },
      });
      if (error) throw error;
      const reply = (data as { reply?: string }).reply ?? "(no reply)";
      const tokens = (data as { tokens_in?: number; tokens_out?: number }).tokens_in ?? 0;
      const tokensOut = (data as { tokens_out?: number }).tokens_out ?? 0;
      const cost = (data as { cost_usd?: number }).cost_usd ?? 0;
      const latency = (data as { latency_ms?: number }).latency_ms ?? 0;
      const degraded = (data as { degraded?: boolean }).degraded;
      const newConvId = (data as { conversation_id?: string | null }).conversation_id;
      if (newConvId) setConvId(newConvId);
      setChat((c) => [...c, { role: "assistant", content: reply, ts: Date.now(), tokens: tokens + tokensOut, cost, latency_ms: latency }]);
      setMemoryContext(`Last reply ${reply.length}ch · tokens ${tokens + tokensOut} · cost $${cost.toFixed(5)} · latency ${latency}ms${degraded ? " · DEGRADED" : ""}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha no chat");
      setChat((c) => [...c, { role: "assistant", content: "[error] não foi possível responder. Verifique a chave LOVABLE_API_KEY." }]);
    } finally {
      setSending(false);
    }
  };

  const successRate = stats && stats.total_calls > 0
    ? ((stats.total_success / stats.total_calls) * 100).toFixed(1)
    : "—";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="p-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2">
            <span className="text-2xl">{agent.emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold">{agent.name}</div>
              <div className="text-[10px] font-mono text-muted-foreground truncate">{agent.role}</div>
            </div>
            <Badge variant={agent.status === "active" ? "default" : "secondary"} className="text-[9px]">
              {agent.status}
            </Badge>
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="chat" className="flex-1 flex flex-col min-h-0">
          <TabsList className="m-3 grid grid-cols-4 w-auto">
            <TabsTrigger value="chat" className="text-[10px] gap-1"><Sparkles className="w-3 h-3" /> Chat</TabsTrigger>
            <TabsTrigger value="history" className="text-[10px] gap-1"><History className="w-3 h-3" /> History</TabsTrigger>
            <TabsTrigger value="stats" className="text-[10px] gap-1"><BarChart3 className="w-3 h-3" /> Stats</TabsTrigger>
            <TabsTrigger value="config" className="text-[10px] gap-1"><Cog className="w-3 h-3" /> Config</TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="flex-1 flex flex-col min-h-0 px-3 pb-3 m-0">
            <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1">
              {chat.length === 0 && (
                <div className="text-center text-xs text-muted-foreground py-8">
                  Inicie uma conversa direta com {agent.name}.
                </div>
              )}
              {chat.map((m, i) => (
                <div
                  key={i}
                  className={cn(
                    "rounded-lg px-3 py-2 text-xs leading-relaxed",
                    m.role === "user"
                      ? "ml-auto max-w-[85%] bg-primary/15 border border-primary/30"
                      : "mr-auto max-w-[90%] bg-surface-2 border border-border",
                  )}
                >
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  {m.role === "assistant" && (m.tokens || m.cost) ? (
                    <div className="mt-1 text-[9px] font-mono text-muted-foreground">
                      {m.tokens ?? 0}t · ${(m.cost ?? 0).toFixed(5)} · {m.latency_ms ?? 0}ms
                    </div>
                  ) : null}
                </div>
              ))}
              {sending && (
                <div className="mr-auto max-w-[90%] bg-surface-2 border border-border rounded-lg px-3 py-2 text-xs flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> {agent.name} está pensando...
                </div>
              )}
            </div>
            <div className="mt-2 border-t border-border pt-2">
              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder={`Pergunte algo a ${agent.name}... (Ctrl+Enter)`}
                  rows={2}
                  className="flex-1 resize-none rounded-md bg-surface-2 border border-border px-2 py-1.5 text-xs outline-none focus:border-primary/60"
                  disabled={sending}
                />
                <Button onClick={send} disabled={sending || !input.trim()} size="sm" className="self-end gap-1">
                  {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Send
                </Button>
              </div>
              {memoryContext && (
                <div className="mt-2 rounded-md border border-info/30 bg-info/5 p-2 text-[10px] font-mono text-info flex items-center gap-1.5">
                  <Brain className="w-3 h-3" /> {memoryContext}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="history" className="flex-1 min-h-0 px-3 pb-3 m-0">
            <ScrollArea className="h-full pr-2">
              {history.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-8">Nenhuma execução registrada ainda.</div>
              ) : (
                <ul className="space-y-1.5">
                  {history.map((h) => (
                    <li
                      key={h.id}
                      className="rounded-md border border-border bg-surface-2/40 p-2 text-[11px] hover:border-primary/40 cursor-pointer"
                      onClick={() => {
                        setConvId(h.id);
                        setChat((h.messages ?? []).map((m) => ({ role: m.role, content: m.content })));
                        toast.success("Conversa carregada no chat");
                      }}
                    >
                      <div className="flex items-center justify-between font-mono text-[10px] text-muted-foreground">
                        <span>{new Date(h.updated_at).toLocaleString()}</span>
                        <span>{h.total_tokens}t · ${Number(h.total_cost_usd).toFixed(4)}</span>
                      </div>
                      <div className="mt-1 truncate text-foreground">
                        {(h.messages?.find((m) => m.role === "user")?.content ?? "(empty)").slice(0, 120)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="stats" className="flex-1 min-h-0 px-3 pb-3 m-0">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Total calls" value={stats?.total_calls ?? 0} />
              <Stat label="Success rate" value={`${successRate}%`} />
              <Stat label="Avg latency" value={`${Math.round(Number(stats?.avg_latency_ms ?? 0))}ms`} />
              <Stat label="Avg cost" value={`$${Number(stats?.avg_cost_usd ?? 0).toFixed(5)}`} />
              <Stat label="Total cost" value={`$${Number(stats?.total_cost_usd ?? 0).toFixed(4)}`} />
              <Stat label="Total tokens" value={(stats?.total_tokens ?? 0).toLocaleString()} />
            </div>
            <div className="mt-3 rounded-md border border-border bg-surface-2/40 p-2 text-[10px] font-mono text-muted-foreground">
              Last call: {stats?.last_called_at ? new Date(stats.last_called_at).toLocaleString() : "never"}
            </div>
          </TabsContent>

          <TabsContent value="config" className="flex-1 min-h-0 px-3 pb-3 m-0 space-y-2">
            <ConfigRow label="Agent ID" value={agent.id} />
            <ConfigRow label="Role" value={agent.role} />
            <ConfigRow label="Models" value={agent.models.join(", ")} />
            <ConfigRow label="Ensemble" value={agent.ensemble ? "yes" : "no"} />
            <ConfigRow label="Status" value={agent.status} />
            <ConfigRow label="Total calls (local)" value={String(agent.calls)} />
            <ConfigRow label="Success (local)" value={`${agent.success}%`} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-border bg-surface-2/40 p-2">
      <div className="text-[9px] font-mono uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className="text-sm font-display font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function ConfigRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 text-[11px] font-mono border-b border-border/40 pb-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground truncate max-w-[60%]" title={value}>{value}</span>
    </div>
  );
}
